import { getAspectRatioStyle } from "@common/controls/AspectRatioContainer/lib";
import { usePrevious } from "@common/lib/hooks";
import classnames from "classnames";
import { isEqual } from "lodash";
import React, { useEffect } from "react";
import { type VideoProps } from "../component";
import { type VideoJsPlayer, type VideoJsPlayerOptions } from "./types";

import loadable from "@loadable/component";

const Videojs = loadable.lib(() => import("video.js"));

import "video.js/dist/video-js.css";

import "./style.less";

type SharedProps = {
  className?: string;

  onReady?: (player: VideoJsPlayer) => void;
  onStartPlay?: () => void;
};

type VideoJsDef = (id: string | Element, options?: VideoJsPlayerOptions, ready?: () => void) => VideoJsPlayer;

type PropsBase = SharedProps & {
  options: VideoJsPlayerOptions;
};

const VideoJsBase = (props: PropsBase & { videojs: VideoJsDef }) => {
  const videoRef = React.useRef<HTMLDivElement>(null);
  const playerRef = React.useRef<VideoJsPlayer | null>(null);
  const { options, onReady } = props;

  const previousSources = usePrevious(options.sources);

  React.useEffect(() => {
    if (!isEqual(options.sources, previousSources) && playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
  }, [options.sources, previousSources]);

  React.useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");
      props.className && videoElement.classList.add(props.className);

      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current!.appendChild(videoElement);

      playerRef.current = props.videojs(videoElement, options, () => {
        onReady && onReady(playerRef.current!);
      });
    }

    options.autoplay && playerRef.current.autoplay(options.autoplay);
    options.sources && playerRef.current.src(options.sources);
    options.poster && playerRef.current.poster(options.poster);
  }, [options, props.videojs]);

  useEffect(() => {
    if (!playerRef.current) return;

    props.onStartPlay && playerRef.current!.on("play", props.onStartPlay);
    return () => {
      props.onStartPlay && playerRef.current!.off("play", props.onStartPlay);
    };
  }, [props.onStartPlay]);

  React.useEffect(() => {
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="video vjs_player styled" data-vjs-player>
      <div className="vjs_player_container" ref={videoRef} />
    </div>
  );
};

const VideoJsLoadableBase = (props: PropsBase) => (
  <Videojs fallback={<div>Loading</div>}>
    {({ default: videojs }) => {
      return <VideoJsBase {...props} videojs={videojs as any} />;
    }}
  </Videojs>
);

type Props = SharedProps &
  VideoProps & {
    posterUrl?: string | null;
  };

const VideoJs = ({ aspectRatio, posterUrl, url, ...baseProps }: Props) => {
  const options = React.useMemo(
    (): VideoJsPlayerOptions => ({
      aspectRatio: typeof aspectRatio === "number" ? `${1 / aspectRatio}/1` : aspectRatio,
      controls: true,
      fill: true,
      poster: posterUrl ?? undefined,
      sources: [
        {
          src: url!,
          type: "application/x-mpegURL"
        }
      ]
    }),
    [aspectRatio, posterUrl, url]
  );

  return (
    <div
      className={classnames("video_container", { background: baseProps.isBackground })}
      style={getAspectRatioStyle(aspectRatio)}
    >
      <div className="video vjs_video">
        <VideoJsLoadableBase {...baseProps} options={options} />

        {baseProps.isBackground && <div className="overlay" />}
      </div>
    </div>
  );
};

export default VideoJs;
